// Tela de Vendas na Visão Geral (#6497): Júlia, Thayla e Valdo.
//
// Pergunta da tela: quem eu atendo agora, e quanto falta para a minha meta?
// A pessoa vê só o próprio resultado, nunca a posição no time (decisão de 27/10/2026).
// Os números reaproveitam o que já existe:
//   - leads, propostas e termos: as mesmas buscas da tela da Gestão Comercial, só da pessoa;
//   - resultado e comissão garantida: a faixa "Resultado do mês" do Kanban (#6426);
//   - meta: a da Folha de Pagamento (a mesma da comissão);
//   - tempo de primeiro contato: a média útil do bônus de SLA do Comercial Interno.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Gift, KanbanSquare, Plus } from 'lucide-react';
import { useResultadoMes } from '../../CRM/Kanban/useResultadoMes';
import { coletarSlaMedioInterno } from '../../../src/lib/comissaoInternaV1';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import { rotuloDoMes, somarMeses } from '../../../src/lib/dashboard/metasMensais';
import { lerMes } from '../../../src/lib/visaoGeral/periodo';
import { LIMITE_SLA_MIN, duracaoCurta, ritmoParaMeta } from '../../../src/lib/visaoGeral/vendas';
import type { TarefaAberta } from '../../../src/lib/visaoGeral/minhasTarefas';
import { BlocosDoDia } from '../BlocosDoDia';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { useMinhasTarefas } from '../useMinhasTarefas';
import { DIAS_SIMULACAO_PARADA, DIAS_TERMO_SEM_ASSINATURA, useHojeComercial, useMetasVendedores } from '../useGestaoComercial';
import { MinhaFila } from './TelaPadrao';
import type { PropsTelaVisaoGeral } from '../tipos';

// Mesmos papéis que o RoleGuard de /tarefas bloqueia (App.tsx): o vendedor trabalha as atividades pelo CRM.
const SEM_ACESSO_TAREFAS = new Set(['vendedor', 'parceiro', 'vendedor_parceiro', 'empresa_parceira_admin', 'empresa_parceira_operador']);
/** Quantos leads a lista do "atender agora" mostra. */
const LEADS_NA_LISTA = 8;

const kwh = (v: number | null | undefined) => formatarValorKpi(v ?? null, 'kwh');
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`;
const dataCurta = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/** Média útil do primeiro contato no mês e no anterior (mesma conta do bônus de SLA). */
function useTempoPrimeiroContato(pessoaId: string, mes: string) {
  const [estado, setEstado] = useState<{ atual: Awaited<ReturnType<typeof coletarSlaMedioInterno>>; anterior: Awaited<ReturnType<typeof coletarSlaMedioInterno>> } | null>(null);
  useEffect(() => {
    let vivo = true;
    setEstado(null);
    const ler = (m: string) => coletarSlaMedioInterno(pessoaId, Number(m.slice(5, 7)), Number(m.slice(0, 4)));
    Promise.all([ler(mes), ler(somarMeses(mes, -1))])
      .then(([atual, anterior]) => { if (vivo) setEstado({ atual, anterior }); })
      .catch(e => { console.error('Tempo de primeiro contato:', e); if (vivo) setEstado({ atual: { mediaUtilMs: null, leadsConcluidos: 0 }, anterior: { mediaUtilMs: null, leadsConcluidos: 0 } }); });
    return () => { vivo = false; };
  }, [pessoaId, mes]);
  return estado;
}

const TelaVendas: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo, aoMudarMes }) => {
  const navigate = useNavigate();
  const { mes, ano, fimDoMes } = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    return { mes: mes0 + 1, ano, fimDoMes: new Date(ano, mes0 + 1, 0) };
  }, [periodo.mes]);
  const acessaTarefas = !SEM_ACESSO_TAREFAS.has(String(pessoa.role));

  // ── dados ──
  const hoje = useHojeComercial(pessoa.id);
  const { dados: tarefas } = useMinhasTarefas(pessoa.id);
  const perfis = useMemo(() => [pessoa], [pessoa]);
  const semEscopo = useCallback(async () => null, []);
  const todosVisiveis = useCallback(() => true, []);
  const resultado = useResultadoMes({
    selecionados: [pessoa.id], escopoResponsaveis: semEscopo, perfis, responsavelVisivel: todosVisiveis, ativo: true, mes: periodo.mes,
  });
  const eu = resultado.atualizadoEm == null ? null : resultado.pessoas.find(p => p.responsavelId === pessoa.id) ?? null;
  const idsMeta = useMemo(() => [pessoa.id], [pessoa.id]);
  const metas = useMetasVendedores(idsMeta, mes, ano);
  const meta = metas ? (metas.get(pessoa.id) || null) : undefined;
  const sla = useTempoPrimeiroContato(pessoa.id, periodo.mes);

  const aprovado = eu ? eu.aprovadas.reduce((t, a) => t + (Number(a.consumo_medio_kwh) || 0), 0) : null;
  const ritmo = aprovado != null && meta !== undefined ? ritmoParaMeta(aprovado, meta, periodo.corrente ? new Date() : fimDoMes) : null;
  const carregandoMeu = aprovado == null || meta === undefined;
  const comercialInterno = eu?.politica === 'interno_v1';

  // Leads: o mais novo primeiro — lead novo esfria rápido, e o relógio do SLA corre igual para todos.
  const leads = hoje.leads ? [...hoje.leads.lista].reverse() : null;
  const foraDoSla = hoje.leads?.lista.filter(l => (l.utilMs ?? 0) > LIMITE_SLA_MIN * 60_000).length ?? 0;

  const abrirTarefa = (t: TarefaAberta) => {
    if (t.negociacao_id) navigate(`/crm?negociacaoId=${t.negociacao_id}`);
    else if (acessaTarefas) navigate(`/tarefas?id=${t.id}`);
    else navigate('/crm');
  };

  const acoes = souEu ? <BotaoAcao rotulo="Nova negociação" icone={Plus} para="/crm?novaNegociacao=1" /> : null;

  // Tempo de primeiro contato contra o limite do bônus (menor é melhor).
  const mediaSla = sla?.atual.mediaUtilMs ?? null;
  const anteriorSla = sla?.anterior.mediaUtilMs ?? null;
  const comparacaoSla = mediaSla != null && anteriorSla != null && anteriorSla > 0 && Math.abs(mediaSla / anteriorSla - 1) >= 0.005
    ? (() => {
        const melhorou = mediaSla < anteriorSla;
        return { texto: `${pct(Math.abs(mediaSla / anteriorSla - 1))} ${melhorou ? 'mais rápido' : 'mais devagar'} que ${rotuloDoMes(somarMeses(periodo.mes, -1)).slice(0, 3)} (${duracaoCurta(anteriorSla)})`, bom: melhorou, sobe: !melhorou };
      })()
    : undefined;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} acoes={acoes} />

      <SecaoVisaoGeral titulo="Hoje" subtitulo={periodo.corrente ? 'o que precisa de você agora' : 'sempre a situação de agora, qualquer que seja o mês'} colunas={3}>
        <CartaoHoje
          valor={hoje.leads ? hoje.leads.total : null}
          rotulo="leads novos para atender"
          tom={foraDoSla > 0 ? 'critico' : 'atencao'}
          para="/crm"
          dica={hoje.leads && hoje.leads.total > 0 ? `Atribuídos a você e sem nenhuma atividade ainda. ${foraDoSla} já passaram de ${LIMITE_SLA_MIN} min úteis.` : 'Leads atribuídos a você sem nenhuma atividade ainda.'}
        />
        <CartaoHoje valor={tarefas ? tarefas.atrasadas.length : null} rotulo="atividades atrasadas" tom="critico" para={acessaTarefas ? '/tarefas' : '/crm'} />
        <CartaoHoje valor={tarefas ? tarefas.hoje.length : null} rotulo="atividades para hoje" tom="atencao" para={acessaTarefas ? '/tarefas' : '/crm'} />
        <CartaoHoje
          valor={hoje.simulacoesParadas}
          rotulo={`simulações paradas há ${DIAS_SIMULACAO_PARADA}+ dias`}
          tom="atencao"
          para="/crm"
          dica={`Simulação feita e nenhuma atividade há ${DIAS_SIMULACAO_PARADA} dias ou mais.`}
        />
        <CartaoHoje
          valor={hoje.termosEnviados}
          rotulo="termos esperando assinatura"
          tom={hoje.termosParados ? 'atencao' : 'neutro'}
          para="/crm"
          dica={hoje.termosEnviados ? `${hoje.termosParados ?? 0} enviados há ${DIAS_TERMO_SEM_ASSINATURA} dias ou mais (contando o último envio).` : undefined}
        />
        <CartaoHoje
          valor={hoje.comPendencia ? hoje.comPendencia.qtd : null}
          rotulo="assinados com pendência na validação"
          tom="atencao"
          para="/crm"
          dica={`Voltaram da validação de dados pedindo documento, garantia ou confirmação do cliente${hoje.comPendencia ? ` (${hoje.comPendencia.documento} de documento, com o selo Pendência documental no Kanban)` : ''}.`}
        />
      </SecaoVisaoGeral>
      {hoje.erro && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">{hoje.erro}</p>}

      <BlocosDoDia pessoa={pessoa} perfil="vendas" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PainelDetalhe titulo="Leads para atender agora" acao={{ rotulo: 'Abrir o CRM', para: '/crm' }}>
          {leads === null ? (
            <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />)}</div>
          ) : leads.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum lead esperando o primeiro contato.</p>
          ) : (
            <>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {leads.slice(0, LEADS_NA_LISTA).map(l => {
                  const fora = (l.utilMs ?? 0) > LIMITE_SLA_MIN * 60_000;
                  return (
                    <li key={l.negociacaoId}>
                      <button type="button" onClick={() => navigate(`/crm?negociacaoId=${l.negociacaoId}`)}
                        className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${fora ? 'bg-red-500' : 'bg-emerald-500'}`} aria-hidden />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{l.cliente || 'Lead sem nome'}</span>
                          {l.origem && <span className="block text-xs text-gray-400">{l.origem}</span>}
                        </span>
                        <span className={`text-xs whitespace-nowrap tabular-nums ${fora ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-emerald-700 dark:text-emerald-400'}`}
                          title={`Atribuído em ${new Date(l.atribuidoEm).toLocaleString('pt-BR')}. Tempo útil: conta só o seu expediente.`}>
                          {duracaoCurta(l.utilMs)} úteis
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Os mais novos primeiro. Verde: ainda dentro de {LIMITE_SLA_MIN} min úteis; vermelho: já passou.
                {leads.length > LEADS_NA_LISTA && ` Mais ${plural(leads.length - LEADS_NA_LISTA, 'lead', 'leads')} no CRM.`}
              </p>
            </>
          )}
        </PainelDetalhe>
        <MinhaFila dados={tarefas} titulo="Minha agenda" aoAbrir={abrirTarefa} />
      </div>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={periodo.rotulo}>
        <CartaoIndicador
          titulo="Meu resultado"
          valor={carregandoMeu ? null : kwh(aprovado)}
          meta={!carregandoMeu && meta ? { texto: `meta ${kwh(meta)} (Folha de Pagamento)`, atingido: aprovado! / meta } : undefined}
          detalhe={eu ? `${plural(eu.aprovadas.length, 'contrato aprovado', 'contratos aprovados')} no mês. Termos assinados no mês: ${eu.assinados.qtd} (${eu.validacao.qtd} em validação, ${eu.pendencia.qtd} com pendência, ${eu.reprovados.qtd} ${eu.reprovados.qtd === 1 ? 'reprovado' : 'reprovados'}).` : undefined}
          para="/crm"
          origem="kWh das negociações aprovadas no mês, a base da comissão (mesma conta da faixa Resultado do mês do CRM)."
        />
        <CartaoIndicador
          titulo="Quanto falta para a meta"
          valor={carregandoMeu ? null : !ritmo ? '—' : ritmo.falta === 0 ? 'Meta batida' : kwh(ritmo.falta)}
          detalhe={carregandoMeu ? undefined
            : !ritmo ? 'Sem meta cadastrada para este mês na Folha de Pagamento.'
            : ritmo.falta === 0 ? `${pct(ritmo.atingido)} da meta.`
            : !periodo.corrente ? `${rotuloDoMes(periodo.mes)} fechou com ${pct(ritmo.atingido)} da meta.`
            : ritmo.porDia == null ? 'Não sobra dia útil neste mês.'
            : `${kwh(ritmo.porDia)} por dia útil até ${dataCurta(fimDoMes)} (${plural(ritmo.diasUteis, 'dia útil', 'dias úteis')}, contando hoje).`}
        />
        <CartaoIndicador
          titulo="Comissão garantida"
          valor={resultado.atualizadoEm == null ? null : eu?.comissao ? reais(eu.comissao.valor) : '—'}
          detalhe={!eu ? undefined : !eu.politica ? 'Sem comissão de vendedor pela Folha de Pagamento.'
            : !eu.comissao ? 'Falta a meta ou a tarifa do mês para calcular.'
            : [
                'Das aprovadas no mês, pelas regras da Folha, como se o mês fechasse hoje.',
                eu.comissao.semPlano ? `${plural(eu.comissao.semPlano, 'aprovada', 'aprovadas')} sem plano definido ainda fora.` : '',
                eu.comissao.grupoA ? `${eu.comissao.grupoA} do Grupo A ${eu.comissao.grupoA === 1 ? 'fica' : 'ficam'} de fora (o gerente calcula à parte).` : '',
                eu.comissao.slaPendente ? 'SLA do mês ainda não cumprido: ativação pela metade.' : '',
              ].filter(Boolean).join(' ')}
        />
        <CartaoIndicador
          titulo="Meu tempo de primeiro contato"
          valor={sla === null ? null : mediaSla == null ? '—' : `${duracaoCurta(mediaSla)} úteis`}
          meta={comercialInterno && mediaSla != null
            ? { texto: `bônus de R$ 100 com média até ${LIMITE_SLA_MIN} min úteis`, atingido: Math.min(1, (LIMITE_SLA_MIN * 60_000) / Math.max(mediaSla, 1)) }
            : undefined}
          detalhe={sla === null ? undefined : sla.atual.leadsConcluidos
            ? `Média de ${plural(sla.atual.leadsConcluidos, 'lead atendido', 'leads atendidos')} no mês, contando só o seu expediente.`
            : 'Nenhum lead atribuído e atendido no mês ainda.'}
          comparacao={comparacaoSla}
          origem="Do lead atribuído à primeira atividade registrada no CRM (nota, status, etapa). Mesma conta do bônus de SLA de Closer e BDR."
        />
      </SecaoVisaoGeral>

      <ListaAtalhos itens={[
        { rotulo: 'CRM', para: '/crm', icone: KanbanSquare },
        { rotulo: 'Indicações', para: '/crm/indicacoes', icone: Gift },
        ...(acessaTarefas ? [{ rotulo: 'Atividades e visitas', para: '/tarefas', icone: ClipboardList }] : []),
      ]} />
    </div>
  );
};

export default TelaVendas;
